import { z } from 'zod';

const PHONE = /^\+[1-9]\d{6,14}$/;

const name = z.string().trim().max(120, { error: 'customers:validation.nameLength' });

/** Staff type phones with spaces and dashes; the backend stores bare E.164. */
export const phone = z
  .string()
  .trim()
  .transform((value) => value.replace(/[\s().-]/g, ''))
  .refine((value) => value === '' || PHONE.test(value), { error: 'customers:validation.phone' });

export const customerCreateSchema = z.object({
  email: z.email({ error: 'customers:validation.email' }),
  firstName: name,
  lastName: name,
  phone,
  acceptsMarketing: z.boolean(),
  tags: z.array(z.string()).max(50, { error: 'customers:validation.tags' }),
  notes: z.string().trim().max(5000, { error: 'customers:validation.notesLength' }),
});

export type CustomerCreateFormInput = z.input<typeof customerCreateSchema>;
export type CustomerCreateFormOutput = z.output<typeof customerCreateSchema>;

/** Guests keep their status until they set a password; staff only switch active and disabled. */
export const customerProfileSchema = z.object({
  firstName: name,
  lastName: name,
  phone,
  acceptsMarketing: z.boolean(),
  savedRingSize: z.string().trim().max(20, { error: 'customers:validation.ringSizeLength' }),
  status: z.enum(['guest', 'active', 'disabled']),
});

export type CustomerProfileFormInput = z.input<typeof customerProfileSchema>;
export type CustomerProfileFormOutput = z.output<typeof customerProfileSchema>;

export const customerEmailSchema = z.object({
  email: z.email({ error: 'customers:validation.email' }),
});

export type CustomerEmailFormInput = z.input<typeof customerEmailSchema>;
export type CustomerEmailFormOutput = z.output<typeof customerEmailSchema>;

/** Blank keeps the store's own account/reset page; anything else must be a full web address. */
export const accountEmailSchema = z.object({
  resetUrl: z
    .string()
    .trim()
    .refine((value) => value === '' || z.url({ protocol: /^https?$/ }).safeParse(value).success, {
      error: 'customers:validation.url',
    }),
});

export type AccountEmailFormInput = z.input<typeof accountEmailSchema>;
export type AccountEmailFormOutput = z.output<typeof accountEmailSchema>;
